import { useEffect, useMemo, useState } from "react";
import { Animation } from "../../../components/Animation/Animation.tsx";
import { BaseDialog } from "../../../components/Dialog/BaseDialog.tsx";
import { useUser } from "../../hooks/useUser.ts";
import { useWorkspace } from "../../hooks/useWorkspace.ts";
import { useWorkspaceDetails } from "../../hooks/useWorkspaceDetails.ts";
import { CancellingReasonsFormContent } from "./CancellingReasonsFormContent.tsx";
import { PricingTable } from "./PricingTable.tsx";
import { upgradeDialogStore } from "./upgradeDialogStore.ts";

type UpgradeStep = "upgrade" | "cancellingReasons";

export const UpgradeDialog = () => {
  const { isDialogOpen } = upgradeDialogStore.useState();
  const [upgradeStep, setUpgradeStep] = useState<UpgradeStep>("upgrade");

  const closeOnClickOutside = upgradeStep === "cancellingReasons";

  useEffect(() => {
    if (isDialogOpen) setUpgradeStep("upgrade");
  }, [isDialogOpen]);

  return (
    <BaseDialog
      isOpen={isDialogOpen}
      onOpenChange={(open) => {
        if (open) {
          upgradeDialogStore.openDialog();
        } else {
          upgradeDialogStore.closeDialog();
        }
      }}
      className="shadow-300"
      escapeKeyDownAuthorized={closeOnClickOutside}
      displayCloseButton={closeOnClickOutside}
      closeOnClickOutside={closeOnClickOutside}
      content={
        isDialogOpen && (
          <DialogContent
            upgradeStep={upgradeStep}
            setUpgradeStep={setUpgradeStep}
          />
        )
      }
    />
  );
};

const DialogContent = ({
  upgradeStep,
  setUpgradeStep,
}: {
  upgradeStep: UpgradeStep;
  setUpgradeStep: (step: UpgradeStep) => void;
}) => {
  const { user } = useUser();
  const { workspace } = useWorkspace();
  const { details } = useWorkspaceDetails({});

  const highlight = useMemo(() => {
    const highlightLabel = "Current plan";
    switch (workspace.type) {
      case "team":
        return {
          type: "team" as const,
          label: highlightLabel,
        };
      case "pro":
        return {
          type: "pro" as const,
          label: highlightLabel,
        };
      case "enterprise":
        return {
          type: "enterprise" as const,
          label: highlightLabel,
        };
      default:
        return undefined;
    }
  }, [workspace.type]);

  return (() => {
    switch (upgradeStep) {
      case "upgrade":
        return (
          <div className="flex-col gap-100 bg-surface-secondary-rest py-400 px-800 rounded-300">
            {user && details && (
              <PricingTable
                workspace={workspace}
                workspaceDetails={details}
                highlight={highlight}
                onClickContinueWithCurrentVersion={() =>
                  setUpgradeStep("cancellingReasons")
                }
              />
            )}
          </div>
        );
      case "cancellingReasons":
        return (
          <div className="flex-row">
            <div className="w-[25%] bg-surface-inverse-rest content-center">
              <Animation
                loadData={() => import("../../../assets/boardInitLoading.json")}
                loop
                autoplay
              />
            </div>
            <CancellingReasonsFormContent
              className="h-[500px] w-[75%]"
              onPreviousClick={() => {
                setUpgradeStep("upgrade");
              }}
            />
          </div>
        );
    }
  })();
};
